import React from 'react';
import moment from 'moment';
import { faRedo } from '@fortawesome/free-solid-svg-icons';
import { strings } from '../../../resources/strings';
import { SeparationType } from '../../../enums/SeparationType';
import { ProfileType } from '../../../enums/ProfileType';
import { StatePrescriptionLog } from '../../../models/StatePrescriptionLog';
import { Order } from '../../../models/Order';
import { StateLog } from '../../../models/StateLog';
import { useOrderUtils } from '../../../hooks/useOrderUtils';
import ServicesContext from '../../../contexts/ServicesContext';
import { Button } from '../../../common-components/Button/Button';
import { If } from '../../../common-components/If/If';
import { BadgeFinancialStatus } from '../../../common-components/BadgeFinancialStatus/BadgeFinancialStatus';
import { OrderStateChangeButtons } from '../../OrderStateChangeButtons/OrderStateChangeButtons';
import { PrescriptionOrderOptionsButton } from '../../PrescriptionOrderOptionsButton/PrescriptionOrderOptionsButton';
import { GeneralOptionsButton } from '../../GeneralOptionsButton/GeneralOptionsButton';
import { Container, StaticField, Title, Value, Options, TabsContainer } from './styles';

interface IHeaderProps {
  order?: Order | null;
  lastStateLog?: StateLog | null;
  lastStatePrescriptionLog?: StatePrescriptionLog | null;
  update: () => void;
  stateOrderChanged: (newStateId?: number) => void;
  statePrescriptionChanged: (newStateId?: number) => void;
  modalOpened?: () => void;
  modalClosed?: (changed?: boolean) => void;
  handleGeneralOption?: (key: any) => void;
  userProfile?: ProfileType;
}

const Header: React.FC<IHeaderProps> = ({
  children,
  order,
  lastStateLog,
  lastStatePrescriptionLog,
  update,
  stateOrderChanged,
  statePrescriptionChanged,
  modalOpened,
  modalClosed,
  handleGeneralOption,
  userProfile
}) => {

  const {
    statesService,
    statesPrescriptionsService
  } = React.useContext(ServicesContext);

  const {
    getStatus,
    orderIsCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOriginAndCanBeSelected
  } = useOrderUtils();

  const lastStateLogDescription = statesService.get().find(x => x.id === lastStateLog?.currentStateId)?.name ||
    statesService.getDelivery().find(x => x.id === lastStateLog?.currentStateId)?.name;

  const lastStatePrescriptionLogDescription = order?.separationType === SeparationType.Prescription
    ? statesPrescriptionsService.get().find(x => +x.id === lastStatePrescriptionLog?.currentStateId)?.description
    : null;

  const isOrderCrossChannelAndCanBeSelected = order && 
    orderIsCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOriginAndCanBeSelected(order);

  return (
    <>
      <Container>
        <StaticField width='85px'>
          <Title>{strings.Number}</Title>
          <Value bold data-id={order?.id}>{order?.number}</Value>
        </StaticField>
        <StaticField width='180px'>
          <Title>{strings.Payment}</Title>
          {order?.paymentAuthorizedAt
            ? <Value bold>{moment(order?.paymentAuthorizedAt).format('DD/MM/YYYY HH:mm:ss')}</Value>
            : <BadgeFinancialStatus financialStatus={order?.financialStatus} nobkcolor />
          }
        </StaticField>
        <StaticField width='120px'>
          <Title>{strings.Situation}</Title>
          <Value bold>{getStatus(order)}</Value>
        </StaticField>
        <StaticField width='250px'>
          <Title>{strings.State}</Title>
          <Value bold>{lastStateLogDescription}</Value>
        </StaticField>
        <If condition={order?.separationType === SeparationType.Prescription}>
          <StaticField width='250px'>
            <Title>{strings.Degree}</Title>
            <Value bold>{lastStatePrescriptionLogDescription || '?'}</Value>
          </StaticField>
        </If>
        <Options>
          <Button icon={faRedo} marginRight='3px' title={strings.Update} click={update} />
          <GeneralOptionsButton
            orderId={order?.id}
            orderNumber={order?.number}
            isPhysicalStore={order?.isPhysicalStore}
            separationType={order?.separationType}
            customerId={order?.customerId}
            customerShopifyId={order?.customer?.shopifyId}
            orderDataSource={order?.dataSource}
            shopifyFormatedClosedAt={order?.shopifyFormatedClosedAt}
            modalOpened={modalOpened}
            modalClosed={modalClosed}
            handleGeneralOption={handleGeneralOption}
            orderIsCrossChannel={order?.isCrossChannelSale}
          />
          <If condition={!order?.shopifyFormatedClosedAt && userProfile !== ProfileType.Viewer && 
              (!order?.isCrossChannelSale || isOrderCrossChannelAndCanBeSelected)}>
            <OrderStateChangeButtons
              orderId={order?.id}
              isPhysicalStore={order?.isPhysicalStore}
              currentStateId={lastStateLog?.currentStateId}
              changed={(newStateId) => stateOrderChanged(newStateId)}
              userProfile={userProfile}
              orderTags={order?.tags}
            />
          </If>
          <If condition={order?.separationType === SeparationType.Prescription}>
            <PrescriptionOrderOptionsButton
              currentStateId={lastStatePrescriptionLog?.currentStateId}
              orderId={order?.id}
              orderNumber={order?.number}
              orderDataSource={order?.dataSource}
              orderIsPhysicalStore={order?.isPhysicalStore}
              customerEmail={order?.customer?.email}
              customerPhone={order?.customer?.phone}
              customerShopifyId={order?.customer?.shopifyId}
              customerId={order?.customerId}
              modalOpened={modalOpened}
              modalClosed={modalClosed}
              statePrescriptionChanged={(newStateId) => statePrescriptionChanged(newStateId)}
              orderTags={order?.tags}
              orderIsCrossChannelSale={order?.isCrossChannelSale}
              orderLocationId={order?.locationId}
              orderOriginLocationId={order?.originLocationId}
            />
          </If>
        </Options>
      </Container>
      <TabsContainer>
        {children}
      </TabsContainer>
    </>
  )
};

export { Header };